import { Box, Button, Modal, Paper, styled } from "@mui/material";
import { cloneDeep } from "lodash";
import { useState, useEffect, Fragment, useContext } from "react";
import EndGameModal from "../../components/endGameModal";
import colors from "../../theme/colors";
import fonts from "../../theme/fonts";
import { GAME_STATE } from "../../types/game";
import { X01GameState } from "../../types/x01";
import { BaseMatchState, GAME_TYPE, MATCH_TYPE } from "../../types/match";
import { PLAYER } from "../../types/players";
import { InOut } from "../../types/x01";
import {
  getX01InitGameState,
  endGame,
  getNextX01State,
  doBotMove,
  getNextX01NumpadState,
  doNumpadBotMove,
} from "../../util/x01Logic";
import ResultScreen from "../results/results";
import PreviousScores from "./previousScores";
import AllButtons from "../../components/AllButtons";
import Numpad from "./numpad";
import { ScreenSizeContext } from "../../contexts/ScreenSizeContext";
import { TEAM, teamOnePlayers, teamTwoPlayers } from "../../types/teams";
import { mediaQueries } from "../../theme/breakpoints";
import { OnlineMatchContext } from "../../contexts/OnlineMatchContext";
import UndoModal from "../../components/undoModal";
import { continueUndoHandling, getNextThrower } from "../../util/matchLogic";

interface X01Props {
  matchState: BaseMatchState;
  gameScore: number;
  inOut: InOut;
  endLeg: Function;
  rematch: Function;
  restart: boolean;
  setRestart: Function;
}

export default function X01({
  matchState,
  gameScore,
  inOut,
  endLeg,
  rematch,
  restart,
  setRestart,
}: X01Props) {
  const { desktop } = useContext(ScreenSizeContext);
  const {
    online,
    onlinePlayer,
    onlineGameState,
    handleX01Hit,
    handleEndLeg,
    handleUndoRequest,
    handleDenyUndo,
  } = useContext(OnlineMatchContext);
  const [gameState, setGameState] = useState<X01GameState>(
    getX01InitGameState(gameScore, inOut, matchState.matchType)
  );
  const [oldStates, setOldStates] = useState<X01GameState[]>([]);
  const [inUndoState, setInUndoState] = useState<boolean>(false);
  const [botThinking, setBotThinking] = useState<boolean>(false);
  const [showTeamModal, setShowTeamModal] = useState<TEAM | null>(null);
  const [undoCanceled, setUndoCanceled] = useState(false);

  matchState.gameType = GAME_TYPE.X01;

  const botTurn =
    matchState.matchType === MATCH_TYPE.BOT &&
    gameState.currentPlayer === PLAYER.SECOND;
  const disableButton =
    botTurn || (online && gameState?.currentPlayer !== onlinePlayer);

  useEffect(() => {
    if (!onlineGameState || onlineGameState === gameState) return;
    setGameState(onlineGameState as X01GameState);
    // eslint-disable-next-line
  }, [onlineGameState]);

  useEffect(() => {
    if (restart) {
      rematchHandler();
    }
    // eslint-disable-next-line
  }, [restart]);

  useEffect(() => {
    if (
      gameState.gameState === GAME_STATE.PENDING ||
      gameState.gameState === GAME_STATE.FINISHED ||
      inUndoState
    )
      return;

    if (
      matchState.matchType === MATCH_TYPE.BOT &&
      gameState.currentPlayer === PLAYER.SECOND
    ) {
      if (gameState.throwNumber === 1) {
        botTimeout();
      } else if (gameState.throwNumber > 1) {
        botTimeout();
      }
    }
    // eslint-disable-next-line
  }, [gameState, inUndoState]);

  const botTimeout = () =>
    setTimeout(() => {
      if (botThinking || inUndoState) return;
      setBotThinking(true);
      const oldGameState = cloneDeep(gameState);
      setOldStates([...oldStates, oldGameState]);
      let botMove: X01GameState;
      if (gameState.inOut !== InOut.MANUAL_NUMPAD) {
        botMove = doBotMove(oldGameState, matchState.botDifficulty);
      } else {
        botMove = doNumpadBotMove(oldGameState, matchState.botDifficulty);
      }
      setGameState(botMove);
      setBotThinking(false);
    }, 1250);

  const onClickHandler = async (number: number, multiplier: number) => {
    if (
      !gameState ||
      gameState.gameState === GAME_STATE.PENDING ||
      gameState.gameState === GAME_STATE.FINISHED
    )
      return;
    let newState: X01GameState;

    if (!online) {
      const oldGameState = cloneDeep(gameState);
      setOldStates([...oldStates, oldGameState]);
      newState = getNextX01State({ number, multiplier }, gameState);
    } else {
      newState = await handleX01Hit({ number, multiplier });
      if (!newState) return;
    }

    setGameState(newState);
    setInUndoState(false);
  };

  const manualScoreHandler = (roundScore: number) => {
    if (
      !gameState ||
      gameState.gameState === GAME_STATE.PENDING ||
      gameState.gameState === GAME_STATE.FINISHED
    )
      return;
    const oldGameState = cloneDeep(gameState);
    setOldStates([...oldStates, oldGameState]);
    const newState = getNextX01NumpadState(roundScore, gameState);
    setGameState(newState);
    setInUndoState(false);
  };

  const undoHandler = async () => {
    if (
      !continueUndoHandling(
        oldStates,
        undoCanceled,
        online,
        onlinePlayer,
        gameState,
        matchState.matchType
      )
    )
      return;

    setInUndoState(true);
    const states = [...oldStates];
    let latestOldState = states[states.length - 1];
    const updatedOldStates: X01GameState[] = states.slice(0, -1);
    if (online) {
      const undoState = await handleUndoRequest();
      if (!Boolean(undoState?.error)) latestOldState = undoState;
    }
    setOldStates(updatedOldStates);
    setGameState(latestOldState);
  };

  const endGameHandler = async () => {
    let newState: X01GameState;

    if (!online) {
      newState = endGame(gameState);
      endLeg(newState);
    } else {
      newState = await handleEndLeg();
    }

    setOldStates([]);
    setGameState(newState);
  };

  const newGameHandler = () => {
    const newGameState = getX01InitGameState(
      gameScore,
      inOut,
      matchState.matchType
    );
    newGameState.currentPlayer = getNextThrower(gameState);
    newGameState.gameState = GAME_STATE.ACTIVE;
    setGameState(newGameState);
  };

  const rematchHandler = () => {
    rematch();
    const newGameState = getX01InitGameState(
      gameScore,
      inOut,
      matchState.matchType
    );
    newGameState.x01Game = gameState.x01Game;
    newGameState.inOut = gameState.inOut;
    setGameState(newGameState);
    setRestart(false);
  };

  return (
    <Wrapper>
      {gameState.gameState !== GAME_STATE.FINISHED && (
        <Fragment>
          <TopRow>
            <PlayerAndScore>
              <PlayerName
                activeThrower={teamOnePlayers.includes(gameState.currentPlayer)}
                player={PLAYER.FIRST}
              >
                {teamOnePlayers.includes(gameState.currentPlayer)
                  ? gameState.currentPlayer
                  : "Team 1"}
              </PlayerName>
              <PlayerScore>{gameState.team1.score}</PlayerScore>
              {!desktop && (
                <Box paddingBottom="5px">
                  <PreviousShotsButton
                    disabled={
                      gameState.team1.previousRounds.filter(
                        (r) => r.roundScore !== 0
                      ).length <= 0
                    }
                    onClick={() => setShowTeamModal(TEAM.ONE)}
                  >
                    Rounds
                  </PreviousShotsButton>
                </Box>
              )}
            </PlayerAndScore>
            <ThrowBox>
              <Box>Throw:</Box>
              <Box>{gameState.throwNumber}</Box>
            </ThrowBox>
            <PlayerAndScore>
              <PlayerName
                activeThrower={teamTwoPlayers.includes(gameState.currentPlayer)}
                player={PLAYER.SECOND}
              >
                {teamTwoPlayers.includes(gameState.currentPlayer)
                  ? gameState.currentPlayer
                  : "Team 2"}
              </PlayerName>
              <PlayerScore>{gameState.team2.score}</PlayerScore>
              {!desktop && (
                <Box paddingBottom="5px">
                  <PreviousShotsButton
                    disabled={
                      gameState.team2.previousRounds.filter(
                        (r) => r.roundScore !== 0
                      ).length <= 0
                    }
                    onClick={() => setShowTeamModal(TEAM.TWO)}
                  >
                    Rounds
                  </PreviousShotsButton>
                </Box>
              )}
            </PlayerAndScore>
          </TopRow>
          <BottomRow>
            <ScoreColumn>
              <PreviousScores
                previousRounds={gameState.team1.previousRounds}
                team={TEAM.ONE}
              />
            </ScoreColumn>
            <ButtonsColumn>
              {gameState.inOut === InOut.MANUAL_NUMPAD ? (
                <Numpad
                  disabled={disableButton}
                  inUndoState={inUndoState}
                  currentPlayer={gameState.currentPlayer}
                  scoreHandler={manualScoreHandler}
                  undoHandler={undoHandler}
                />
              ) : (
                <AllButtons
                  onClickHandler={onClickHandler}
                  currentPlayer={gameState.currentPlayer}
                  botTurn={disableButton}
                  undoHandler={undoHandler}
                  inUndoState={inUndoState}
                />
              )}
            </ButtonsColumn>
            <ScoreColumn>
              <PreviousScores
                previousRounds={gameState.team2.previousRounds}
                team={TEAM.TWO}
              />
            </ScoreColumn>
          </BottomRow>
        </Fragment>
      )}
      {gameState.gameState === GAME_STATE.FINISHED && (
        <ResultScreen
          newGameHandler={newGameHandler}
          matchState={matchState}
          rematchHandler={rematchHandler}
        />
      )}
      <EndGameModal
        open={
          gameState.gameState === GAME_STATE.PENDING &&
          (online ? onlinePlayer === gameState.currentPlayer : true)
        }
        undo={undoHandler}
        endGame={endGameHandler}
      />
      <UndoModal
        open={
          Boolean(gameState.requests?.undo) &&
          !undoCanceled &&
          gameState.requests?.undo !== onlinePlayer
        }
        undo={undoHandler}
        cancel={async () => {
          await handleDenyUndo();
          setUndoCanceled(true);
        }}
        requestedPlayer={gameState.requests?.undo}
      />
      {showTeamModal && (
        <Modal
          open={showTeamModal !== null}
          onClose={() => {
            setShowTeamModal(null);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Paper elevation={6}>
            <Box sx={style}>
              <PreviousScores
                previousRounds={gameState[showTeamModal].previousRounds}
                team={showTeamModal}
                modal={true}
              />
            </Box>
          </Paper>
        </Modal>
      )}
    </Wrapper>
  );
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50vw",
  height: "70vh",
  maxWidth: 400,
  bgcolor: colors.lightGreen,
  boxShadow: 24,
  p: 4,
  display: "flex",
  gap: "8px",
};

const Wrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  height: "inherit",
  "@media (max-width: 600px)": {
    overflowX: "hidden",
  },
});

const TopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-around",
  fontSize: fonts.size.medium,
  fontFamily: fonts.family.chalk,
  color: colors.lightBeige,
  "@media (max-width: 600px)": {
    fontSize: fonts.size.small,
  },
});

const BottomRow = styled(Box)({
  display: "flex",
  flex: 5,
  justifyContent: "space-evenly",
  borderTop: "4px double black",
  maxHeight: `${window.innerHeight * 0.8}px`,
  "@media(max-width: 960px)": {
    border: "none",
    // padding: "0px 10px"
    // maxWidth: "95%",
  },
  "@media(max-width: 650px)": {
    width: "95%",
    margin: "0 auto",
    // alignSelf: "center"
  },
});

const PlayerAndScore = styled(Box)({
  display: "flex",
  flexDirection: "column",
  fontSize: fonts.size.mediumLarge,
  width: "100%",
  justifyContent: "center",
});

const PlayerName = styled(Box)<{ activeThrower: boolean; player: PLAYER }>(
  ({ activeThrower, player }) => ({
    borderBottom: "4px double black",
    color: !activeThrower
      ? colors.transparentLightBeige
      : player === PLAYER.FIRST
      ? colors.darkGray
      : colors.lightBeige,
    backgroundColor: !activeThrower
      ? "transparent"
      : player === PLAYER.FIRST
      ? colors.lightBeige
      : colors.darkGray,
  })
);

const PlayerScore = styled(Box)({
  fontSize: fonts.size.extraLarge,
  [mediaQueries.mobile]: {
    fontSize: fonts.size.large,
  },
});

const ThrowBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  color: colors.lightBeige,
  border: "4px double black",
  padding: " 5px 25px",
  width: "200px",
  fontSize: fonts.size.mediumLarge,
  [mediaQueries.mobile]: {
    fontSize: fonts.size.medium,
    width: "130px",
  },
});

const ScoreColumn = styled(Box)({
  display: "flex",
  flex: 1,
  maxHeight: `100%`,
  "@media(max-width: 960px)": {
    display: "none",
  },
});

const ButtonsColumn = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flex: 4,
  justifyContent: "center",
  height: "100%",
  "@media(max-width: 600px)": {
    maxWidth: "90vw",
  },
});

const PreviousShotsButton = styled(Button)({
  backgroundColor: colors.lightGreen,
  color: colors.darkGray,
  width: "90%",
  textTransform: "none",
});
